import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Divider,
    Button
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getBookingById } from '../../Apis/BookingsApi';

const BookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBooking = async () => {
            try {
                const data = await getBookingById(id);
                setBooking(data.bookings || null);
            } catch (error) {
                console.error("Failed to fetch booking:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBooking();
    }, [id]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <CircularProgress />
            </Box>
        );
    }

    if (!booking) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography variant="h6">Booking not found.</Typography>
                <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                    Go Back
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3, maxWidth: 1000, margin: "auto" }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Booking Details
            </Typography>

            {/* Booking Info */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Booking Information</Typography>
                <Divider sx={{ mb: 1 }} />
                <Typography><b>Booking ID:</b> {booking.bookingId || "-"}</Typography>
                <Typography><b>Booking Type:</b> {booking.booking_type || "-"}</Typography>
                <Typography><b>Amount:</b> ₹{booking.amount || "0.00"}</Typography>
                <Typography><b>Delivery Price:</b> ₹{booking.delivery_price || "0.00"}</Typography>
                <Typography><b>Handling Price:</b> ₹{booking.handlingPrice || "0.00"}</Typography>
                <Typography><b>Membership Discount:</b> ₹{booking.membership_discount || "0.00"}</Typography>
                <Typography><b>Total Amount:</b> ₹{booking.total_amout || "0.00"}</Typography>
                <Typography><b>Total Weight:</b> {booking.total_weightInKg || 0} kg</Typography>
                <Typography><b>Status:</b> {booking.status || "-"}</Typography>
                <Typography><b>Payment Done:</b> {booking.isPayment ? "Yes" : "No"}</Typography>
                <Typography><b>Created At:</b> {booking.createdAt || "-"}</Typography>
                <Typography><b>Updated At:</b> {booking.updatedAt || "-"}</Typography>
            </Paper>

            {/* Store Details */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Store Details</Typography>
                <Divider sx={{ mb: 1 }} />
                <Typography><b>Name:</b> {booking.store?.name || "-"}</Typography>
            </Paper>

            {/* User Details */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>User Details</Typography>
                <Divider sx={{ mb: 1 }} />
                <Typography><b>Name:</b> {booking.user?.name || "-"}</Typography>
                <Typography><b>Mobile:</b> {booking.user?.mobile || "-"}</Typography>
                <Typography><b>Email:</b> {booking.user?.email || "Not provided"}</Typography>
            </Paper>

            {/* User Address */}
            {booking.user_address && Object.keys(booking.user_address).length > 0 && (
                <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>User Address</Typography>
                    <Divider sx={{ mb: 1 }} />
                    {Object.entries(booking.user_address).map(([key, value]) => (
                        <Typography key={key}><b>{key.charAt(0).toUpperCase() + key.slice(1)}:</b> {value || "-"}</Typography>
                    ))}
                </Paper>
            )}

            {/* Product Details */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Product Details</Typography>
                <Divider sx={{ mb: 1 }} />
                {booking.booking_items && booking.booking_items.length > 0 ? (
                    booking.booking_items.map((item) => (
                        <Box key={item.id} sx={{ mb: 1, p: 1, borderBottom: "1px solid #eee" }}>
                            <Typography><b>Name:</b> {item.product?.name || "-"}</Typography>
                            <Typography><b>Price:</b> ₹{item.price || "0.00"}</Typography>
                            <Typography><b>Quantity:</b> {item.quantity || 0}</Typography>
                            <Typography><b>Weight:</b> {item.product_weight || 0} kg</Typography>
                        </Box>
                    ))
                ) : (
                    <Typography>No products found.</Typography>
                )}
            </Paper>

            {/* Booking Time Slots */}
            <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Booking Time Slots</Typography>
                <Divider sx={{ mb: 1 }} />
                {booking["booking_time-slots"] && booking["booking_time-slots"].length > 0 ? (
                    booking["booking_time-slots"].map((slot, index) => (
                        <Box key={index}>
                            <Typography><b>Date:</b> {slot.bookingDate || "-"}</Typography>
                            <Typography><b>Time:</b> {slot.bookingTime || "-"}</Typography>
                        </Box>
                    ))
                ) : (
                    <Typography>No time slots assigned.</Typography>
                )}
            </Paper>

            {/* Go Back Button */}
            <Box textAlign="center">
                <Button variant="contained" onClick={() => navigate(-1)}>
                    Go Back
                </Button>
            </Box>
        </Box>
    );
};

export default BookingDetails;

